import { type HTMLAttributes, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
    ColorBrandBlue1,
    ColorNeutralDarkGrey,
    ColorNeutralWhite,
    Spacing4,
    Spacing6,
    Spacing8,
    Spacing24
} from 'autogen/design-tokens/tokens';
import { SocialHeadline } from 'autogen/translation-keys/trans-website-articleblock';
import { Typography } from 'components/generic/typography';
import Flex from 'components/Layout/components/Flex';
import dynamic from 'helpers/dynamic';
import { removeListStyling } from 'modules/helpers/mixins';

const FacebookIcon = dynamic(() => import('icons/social/Facebook.svg?react'));
const PinterestIcon = dynamic(() => import('icons/social/Pinterest.svg?react'));

// #region Styles
const FlexColumn = styled(Flex.Column)`
    background-color: ${ColorNeutralWhite};
    padding: ${Spacing24};
`;

const Headline = styled(Typography.Headline)`
    width: 100%;
    text-align: center;
    color: ${ColorBrandBlue1};
`;

const ShareLinkList = styled.ul`
    ${removeListStyling}

    display: flex;
    gap: ${Spacing8};
    justify-content: center;
`;

const ShareLinkAnchor = styled.a`
    display: flex;
    gap: ${Spacing4};
    width: 26px;
    height: 26px;
    border-radius: 100%;
    border: 1px solid ${ColorNeutralDarkGrey};
    padding: ${Spacing6};

    svg {
        width: 100%;
        height: 100%;
    }
`;
// #endregion Styles

type ShareArticleCardType = {
    shareUrl?: string;
};

type SocialMediaType = 'Pinterest' | 'Facebook';

type ShareLinkType = ShareArticleCardType & {
    type: SocialMediaType;
} & HTMLAttributes<HTMLElement>;

const createUrl = (url: string, type: SocialMediaType) => {
    switch (type) {
        case 'Pinterest':
            return `http://www.pinterest.com/pin/create/button/?url=${url}`;
        case 'Facebook':
            return `https://www.facebook.com/sharer/sharer.php?u=${url}`;
    }
};

function ShareLink({ shareUrl, type }: ShareLinkType) {
    const [currentUrl, setCurrentUrl] = useState<string | undefined>(shareUrl ? createUrl(shareUrl, type) : undefined);

    const Icon = (): JSX.Element | null => {
        switch (type) {
            case 'Pinterest':
                return <PinterestIcon />;
            case 'Facebook':
                return <FacebookIcon />;
            default:
                return null;
        }
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: Should only run on render
    useEffect(() => {
        if (!currentUrl) {
            setCurrentUrl(createUrl(window.location.href, type));
        }
    }, []);

    return (
        <ShareLinkAnchor href={currentUrl} target="_blank" rel="noreferrer">
            <Icon />
        </ShareLinkAnchor>
    );
}

export default function ShareArticleCard({ shareUrl, ...restProps }: ShareArticleCardType) {
    const { t } = useTranslation();

    return (
        <FlexColumn gap={Spacing24} {...restProps}>
            <Headline size="S" forwardedAs="h4">
                {t(SocialHeadline)}
            </Headline>

            <ShareLinkList>
                <li>
                    <ShareLink shareUrl={shareUrl} type="Facebook" />
                </li>

                <li>
                    <ShareLink shareUrl={shareUrl} type="Pinterest" />
                </li>
            </ShareLinkList>
        </FlexColumn>
    );
}
